import { useState } from "react";
import { Mail, MessageCircle, Facebook, Instagram, MapPin, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { EMAIL, WHATSAPP_URL, FACEBOOK_URL, INSTAGRAM_URL, ZONE } from "@/lib/constants";
import { Logo } from "@/components/Logo";
import { CallbackModal } from "@/components/CallbackModal";

export const Footer = () => {
  const [callbackOpen, setCallbackOpen] = useState(false);
  const colTitle = "text-xs font-bold uppercase tracking-[0.22em] text-cognac-light";
  const colLink = "w-fit text-left text-sm text-white/65 transition-colors duration-300 hover:text-wood";

  return (
    <footer data-testid="footer" className="bg-navy-deep text-white">
      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-16">
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1.2fr]">
          <div>
            <Logo boxed className="h-9 w-auto" boxClassName="rounded-2xl px-4 py-2.5" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/55">
              Un seul accompagnement pour simplifier tout votre projet de
              rénovation, d'aménagement ou de construction.
            </p>
            <div className="mt-7 flex flex-col gap-3">
              <Link data-testid="footer-link-apropos" to="/a-propos" className={colLink}>À propos</Link>
              <Link data-testid="footer-link-process" to="/comment-ca-marche" className={colLink}>Comment ça marche</Link>
              <Link data-testid="footer-link-realisations" to="/realisations" className={colLink}>Nos réalisations</Link>
            </div>
            <p className="mt-7 text-[11px] font-bold uppercase tracking-[0.22em] text-cognac-light">Suivez-nous</p>
            <div className="mt-3 flex gap-3">
              <a
                data-testid="footer-facebook"
                href={FACEBOOK_URL}
                target="_blank"
                rel="noreferrer"
                title="Facebook CozyHome"
                aria-label="Facebook CozyHome"
                className="rounded-full border border-cognac/40 p-2.5 text-cognac-light transition-colors duration-300 hover:border-cognac hover:bg-cognac hover:text-white"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                data-testid="footer-instagram"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                title="Instagram CozyHome"
                aria-label="Instagram CozyHome"
                className="rounded-full border border-cognac/40 p-2.5 text-cognac-light transition-colors duration-300 hover:border-cognac hover:bg-cognac hover:text-white"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                data-testid="footer-whatsapp"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                aria-label="WhatsApp CozyHome"
                className="rounded-full border border-white/15 p-2.5 text-white/70 transition-colors duration-300 hover:border-cognac hover:bg-cognac hover:text-white"
              >
                <MessageCircle className="h-4 w-4" />
              </a>
            </div>
          </div>

          <nav aria-label="Services">
            <p className={colTitle}>Services</p>
            <div className="mt-6 flex flex-col gap-3">
              <Link data-testid="footer-service-0" to="/services" className={colLink}>Rénovation</Link>
              <Link data-testid="footer-service-1" to="/services" className={colLink}>Extension</Link>
              <Link data-testid="footer-service-2" to="/services" className={colLink}>Aménagement intérieur</Link>
              <Link data-testid="footer-service-3" to="/modeles" className={colLink}>Construction</Link>
            </div>
          </nav>

          <nav aria-label="Ressources">
            <p className={colTitle}>Ressources</p>
            <div className="mt-6 flex flex-col gap-3">
              <Link data-testid="footer-link-conseils" to="/conseils" className={colLink}>Conseils</Link>
              <Link data-testid="footer-link-faq" to="/faq" className={colLink}>FAQ</Link>
              <Link data-testid="footer-suivi-link" to="/suivi" className={colLink}>Espace client</Link>
              <Link data-testid="footer-link-espace-partenaire" to="/acces-phase3?role=partner" className={colLink}>Espace partenaire</Link>
              <Link data-testid="footer-link-espace-entreprise" to="/acces-phase3?role=company" className={colLink}>Espace entreprise</Link>
              <Link data-testid="footer-link-espace-centrale" to="/centrale" className={colLink}>Espace Centrale</Link>
            </div>
          </nav>

          <div>
            <p className={colTitle}>Contact</p>
            <div className="mt-6 flex flex-col gap-4">
              <p className="text-sm leading-relaxed text-white/75">
                Un projet ? Une question ? Laissez-nous vos coordonnées, un conseiller CozyHome vous rappelle.
              </p>
              <button
                data-testid="footer-callback-button"
                onClick={() => setCallbackOpen(true)}
                className="flex w-fit items-center gap-2 rounded-full bg-cognac px-5 py-2.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-white hover:text-navy"
              >
                <Phone className="h-4 w-4" /> Être rappelé
              </button>
              <a data-testid="footer-email" href={`mailto:${EMAIL}`} className="flex items-center gap-3 text-sm text-white/75 transition-colors duration-300 hover:text-wood">
                <Mail className="h-4 w-4 shrink-0 text-cognac-light" /> {EMAIL}
              </a>
              <p data-testid="footer-zone" className="flex items-start gap-3 text-sm text-white/75">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cognac-light" />
                Zone d'intervention : {ZONE}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-7">
          <p className="text-xs text-white/40">© 2026 CozyHome — Tous droits réservés</p>
          <div className="flex items-center gap-6">
            <Link data-testid="footer-legal" to="/mentions-legales" className="text-xs text-white/40 transition-colors duration-300 hover:text-white">
              Mentions légales
            </Link>
            <Link data-testid="footer-privacy" to="/politique-confidentialite" className="text-xs text-white/40 transition-colors duration-300 hover:text-white">
              Politique de confidentialité
            </Link>
            <Link data-testid="footer-cookies" to="/cookies" className="text-xs text-white/40 transition-colors duration-300 hover:text-white">
              Gestion des cookies
            </Link>
            <Link
              data-testid="footer-admin-link"
              to="/centrale"
              className="text-xs font-semibold text-white/30 transition-colors duration-300 hover:text-cognac-light"
            >
              Espace pro
            </Link>
          </div>
        </div>
      </div>
      <CallbackModal open={callbackOpen} onClose={() => setCallbackOpen(false)} />
    </footer>
  );
};
